import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, Renderer2, ViewChild } from '@angular/core';
import { DwImageResizeOptions, DwRoMediaEntity } from '@devwareapps/devware-cap';

export interface LoginSideImagePickerOption {
  imageHeight?: number;
  imageWidth?: number;
  isCurrent?: boolean;
  isCustom?: boolean;
  isDefault?: boolean;
  label: string;
  mediaId?: number;
  mediaPath?: string;
  previewUrl?: string;
  standardLoginMediaId?: number;
}

@Component({
  selector: 'app-login-side-image-picker',
  templateUrl: './login-side-image-picker.component.html',
  styleUrls: ['./login-side-image-picker.component.scss']
})
export class LoginSideImagePickerComponent implements OnDestroy {
  @ViewChild('currentCard', { static: false }) currentCardRef: ElementRef<HTMLDivElement>;
  @ViewChild('panel', { static: false }) panelRef: ElementRef<HTMLDivElement>;
  @Input() currentOption: LoginSideImagePickerOption;
  @Input() compact = false;
  @Input() customMedia: DwRoMediaEntity;
  @Input() customOptions: LoginSideImagePickerOption[] = [];
  @Input() isReadOnly = false;
  @Input() mediaGroupId?: number;
  @Input() resizeOptions: DwImageResizeOptions;
  @Input() standardOptions: LoginSideImagePickerOption[] = [];

  @Output() customMediaSelected = new EventEmitter<DwRoMediaEntity>();
  @Output() mediaGroupIdChange = new EventEmitter<number>();
  @Output() standardOptionSelected = new EventEmitter<LoginSideImagePickerOption>();

  private appendedToBody = false;
  private openedAt = 0;
  private uploadSelectionActive = false;
  private uploadSelectionTimeout: number;
  pickerOpen = false;

  constructor(
    private renderer: Renderer2,
  ) {}

  get currentLabel(): string {
    if (this.currentOption?.isCustom) {
      return 'Custom';
    }

    return this.currentOption?.label || 'Not Set';
  }

  get selectedTileLabel(): string {
    if (this.currentOption?.isCustom) {
      return 'Custom';
    }

    return this.currentOption?.label || 'Selected Image';
  }

  get availableStandardOptions(): LoginSideImagePickerOption[] {
    const selectedStandardLoginMediaId = this.currentOption?.isCustom ? null : this.currentOption?.standardLoginMediaId;

    return (this.standardOptions || []).filter(option => option.standardLoginMediaId !== selectedStandardLoginMediaId);
  }

  get availableCustomOptions(): LoginSideImagePickerOption[] {
    const selectedMediaId = this.currentOption?.isCustom ? this.currentOption?.mediaId : null;

    return (this.customOptions || []).filter(option => option.mediaId !== selectedMediaId);
  }

  togglePicker(): void {
    if (this.isReadOnly) {
      return;
    }

    this.pickerOpen = !this.pickerOpen;

    if (this.pickerOpen) {
      this.openedAt = Date.now();

      setTimeout(() => {
        this.attachPanelToBody();
        this.positionPanel();
      });
    } else {
      this.detachPanelFromBody();
    }
  }

  selectStandardOption(option: LoginSideImagePickerOption): void {
    this.standardOptionSelected.emit(option);
    this.closePicker();
  }

  selectCustomMedia(media: DwRoMediaEntity): void {
    this.clearUploadSelectionState();
    this.customMediaSelected.emit(media);
    this.closePicker();
  }

  selectCustomOption(option: LoginSideImagePickerOption): void {
    this.standardOptionSelected.emit(option);
    this.closePicker();
  }

  setMediaGroupId(mediaGroupId: number): void {
    if (!mediaGroupId || mediaGroupId === this.mediaGroupId) {
      return;
    }

    this.mediaGroupId = mediaGroupId;
    this.mediaGroupIdChange.emit(mediaGroupId);
  }

  getImageStyle(option: LoginSideImagePickerOption): any {
    return {
      'background-image': option?.previewUrl ? `url(${option.previewUrl})` : 'none'
    };
  }

  outsideClick(): void {
    if (Date.now() - this.openedAt < 150) {
      return;
    }

    if (this.uploadSelectionActive) {
      return;
    }

    this.closePicker();
  }

  closePicker(): void {
    if (this.uploadSelectionActive) {
      return;
    }

    this.pickerOpen = false;
    this.detachPanelFromBody();
  }

  beginCustomUpload(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.uploadSelectionActive = true;
    this.openedAt = Date.now();
    this.clearUploadSelectionTimeout();
    this.uploadSelectionTimeout = window.setTimeout(() => {
      this.uploadSelectionActive = false;
      this.uploadSelectionTimeout = null;
    }, 10000);
  }

  ngOnDestroy(): void {
    this.clearUploadSelectionState();
    this.detachPanelFromBody();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowPositionChange(): void {
    if (this.pickerOpen) {
      this.positionPanel();
    }
  }

  private attachPanelToBody(): void {
    if (!this.panelRef?.nativeElement || this.appendedToBody) {
      return;
    }

    this.renderer.appendChild(document.body, this.panelRef.nativeElement);
    this.appendedToBody = true;
  }

  private detachPanelFromBody(): void {
    if (!this.panelRef?.nativeElement || !this.appendedToBody) {
      return;
    }

    this.renderer.removeChild(document.body, this.panelRef.nativeElement);
    this.appendedToBody = false;
  }

  private clearUploadSelectionState(): void {
    this.uploadSelectionActive = false;
    this.clearUploadSelectionTimeout();
  }

  private clearUploadSelectionTimeout(): void {
    if (this.uploadSelectionTimeout) {
      clearTimeout(this.uploadSelectionTimeout);
      this.uploadSelectionTimeout = null;
    }
  }

  private positionPanel(): void {
    if (!this.currentCardRef?.nativeElement || !this.panelRef?.nativeElement) {
      return;
    }

    const cardRect = this.currentCardRef.nativeElement.getBoundingClientRect();
    const panelElement = this.panelRef.nativeElement;
    const viewportPadding = 16;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - panelElement.offsetWidth - viewportPadding);
    const left = Math.max(viewportPadding, Math.min(cardRect.left, maxLeft));
    const top = Math.max(viewportPadding, cardRect.top);

    this.renderer.setStyle(panelElement, 'left', `${left}px`);
    this.renderer.setStyle(panelElement, 'top', `${top}px`);
  }
}
